export type TableMode = 'TABLE' | 'MAP'

export type AlertLevel = 'None' | 'Yellow' | 'Amber' | 'Red'
export type SeaConditionLevel = 'None' | 'Yellow' | 'Amber' | 'Red'
export type LightningRisk = 'None' | 'Low' | 'Moderate' | 'High'

export interface AreaRow {
    id: string
    code: string          // new: the area's code, used to match the previous-alert API
    parentId: string | null
    depth: number
    name: string
    level: UkAdminType | string
    levelNo: 1 | 2 | 3 | 4
    previousAlert: AlertLevel
    newAlert: AlertLevel
    rainfall: number
    minWindSpeed: number
    maxWindSpeed: number
    minWaveHeight: number
    maxWaveHeight: number
    seaCondition: SeaConditionLevel
    lightning: LightningRisk
    startDate: string
    endDate: string
    selected: boolean
    continued: boolean    // user chose to continue the active warning with the same severity
}

export const ALERT_LEVELS: AlertLevel[] = ['None', 'Yellow', 'Amber', 'Red']
export const SEA_CONDITION_LEVELS: SeaConditionLevel[] = ['None', 'Yellow', 'Amber', 'Red']
export const LIGHTNING_LEVELS: LightningRisk[] = ['None', 'Low', 'Moderate', 'High']

export type WarningType = 'HRW' | 'HWN' | 'FLW' | 'LSW' | 'LIG' | 'MWA' | 'TSW'

export type ColumnKind = 'prevAlert' | 'newAlert' | 'rainfall' | 'maxWindSpeed' | 'minWindSpeed' | 'lightning' | 'startDate' | 'endDate' | 'minWaveHeight' | 'maxWaveHeight' | 'seaCondition'

export interface ColumnDef {
    kind: ColumnKind
    label: string
}

export const WARNING_LABELS: Record<WarningType, string> = {
    HRW: 'HRW - Heavy Rainfall Warning',
    HWN: 'HWN - High Wind Warning',
    FLW: 'FLW - Flood Warning',
    LSW: 'LSW - Land Slide Warning',
    LIG: 'LIG - Lightning Risk Warning',
    MWA: 'MWA - Marin Weather Warning',
    TSW: 'TSW - Tsunami Warning',
}

const COMMON_START: ColumnDef[] = [
    { kind: 'prevAlert', label: 'Previous Alert Level' },
    { kind: 'newAlert', label: 'New Alert Level' },
]
const COMMON_END: ColumnDef[] = [
    { kind: 'startDate', label: 'Start Date' },
    { kind: 'endDate', label: 'End Date' },
]

// which columns each warning type shows
export const WARNING_COLUMNS: Record<WarningType, ColumnDef[]> = {
    HRW: [
        ...COMMON_START,
        { kind: 'rainfall', label: 'Rainfall (mm)' },
        { kind: 'lightning', label: 'Lightning Risk Level' },
        ...COMMON_END,
    ],
    HWN: [
        ...COMMON_START,
        { kind: 'minWindSpeed', label: 'Min Wind Speed (km/h)' },
        { kind: 'maxWindSpeed', label: 'Max Wind Speed (km/h)' },
        { kind: 'lightning', label: 'Lightning Risk Level' },
        ...COMMON_END,
    ],
    FLW: [
        ...COMMON_START,
        { kind: 'rainfall', label: 'Rainfall (mm)' },
        { kind: 'lightning', label: 'Lightning Risk Level' },
        ...COMMON_END,
    ],
    LSW: [
        ...COMMON_START,
        { kind: 'rainfall', label: 'Rainfall (mm)' },
        { kind: 'lightning', label: 'Lightning Risk Level' },
        ...COMMON_END,
    ],
    LIG: [
        ...COMMON_START,
        { kind: 'lightning', label: 'Lightning Risk Level' },
        ...COMMON_END,
    ],
    MWA: [
        ...COMMON_START,
        { kind: 'minWindSpeed', label: 'Min Wind Speed (km/h)' },
        { kind: 'maxWindSpeed', label: 'Max Wind Speed (km/h)' },
        { kind: 'minWaveHeight', label: 'Min Wave Height (m)' },
        { kind: 'maxWaveHeight', label: 'Max Wave Height (m)' },
        { kind: 'lightning', label: 'Lightning Risk Level' },
        ...COMMON_END,
    ],
    TSW: [
        ...COMMON_START,
        { kind: 'minWindSpeed', label: 'Min Wind Speed (km/h)' },
        { kind: 'maxWindSpeed', label: 'Max Wind Speed (km/h)' },
        { kind: 'minWaveHeight', label: 'Min Wave Height (m)' },
        { kind: 'maxWaveHeight', label: 'Max Wave Height (m)' },
        { kind: 'seaCondition', label: 'Sea Condition Level' },
        { kind: 'lightning', label: 'Lightning Risk Level' },
        ...COMMON_END,
    ],
}

export type UkAdminType =
    | "Country (UK)" | "Country"
    | "County" | "Unitary authority" | "Metropolitan district" | "London borough"
    | "Council area" | "Principal area" | "District"
    | "Non-metropolitan district"
    | "Electoral ward" | "Electoral division" | "Ward" | "Multi-member ward"

// what this area means compared with its active (previous) warning
export type AreaAction = 'NEW' | 'CONTINUE' | 'UPGRADE' | 'DOWNGRADE'

export const ALERT_RANK: Record<AlertLevel, number> = { None: 0, Yellow: 1, Amber: 2, Red: 3 }

export const actionFor = (r: Pick<AreaRow, 'previousAlert' | 'newAlert'>): AreaAction => {
    if (r.previousAlert === 'None') return 'NEW'
    const p = ALERT_RANK[r.previousAlert]
    const n = ALERT_RANK[r.newAlert]
    if (n === p) return 'CONTINUE'
    return n > p ? 'UPGRADE' : 'DOWNGRADE'
}